import { Observable, Subscription } from "rxjs";
import { skip } from "rxjs/operators";
import { select, Store } from "@ngrx/store";
import {
  AppState,
  contractsExcelListState,
  contractsTotalState,
  loadedContractsListState,
  getLoadingContractsState,
} from "src/app/store/reducers";

import { ContractDTO } from "src/app/models/Contracts/contractDTO";
import {
  ColumnSorterInfo,
  ConfigScope,
  ConfigSettingType,
  ExportEvent,
  FieldType,
  FormatType,
  TableConfig,
  TableEvent,
} from "src/app/models/custom-table/custom-table.model";

import { CHIP_STYLE, getChipStyle } from "../../utils/customTableUtils";
import {
  getContractTableConfig,
  saveContractTableConfig,
} from "src/app/store/actions/contracts/contracts.actions";

export class BaseContractsList {
  isLoading$: Observable<boolean>;

  columnInfo$: Observable<ColumnSorterInfo[]>;

  contractList: ContractDTO[];
  contractList$: Observable<ContractDTO[]>;
  excelContractList$: Observable<ContractDTO[]>;
  totalContracts$: Observable<number>;

  loadedContractSub: Subscription;

  formPath: string = "contracts/contract";
  tableTitle: string;

  tableConfig: TableConfig[] = [
    {
      header: "CustomGrid.Options",
      field: FieldType.OPTIONS,
      fieldType: FieldType.OPTIONS,
    },
    {
      header: "Contracts.contractOverallStatusDescription",
      field: "contractOverallStatusDescription",
      fieldType: FieldType.CHIP_STRING,
    },
    {
      header: "Contracts.code",
      field: "code",
      fieldType: FieldType.STRING,
      identifier: true,
      hidden: true,
    },
    {
      header: "Contracts.ContractNumber",
      field: "contractNumber",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "Contracts.Description",
      field: "description",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "Contracts.Status",
      field: "isEnabled",
      fieldType: FieldType.BOOLEAN,
      hidden: true,
    },
    {
      header: "Contracts.PlafondRuleCode",
      field: "plafond.plafondRuleDescription",
      fieldType: FieldType.CHIP_STRING,
    },
    {
      header: "Contracts.AvailablePlafondPercentage",
      field: "plafond.availablePercentage",
      fieldType: FieldType.NUMBER,
      hidden: true,
    },
    {
      header: "Contracts.AvailablePlafond",
      field: "availablePlafond",
      fieldType: FieldType.PROGRESS,
      hidden: false,
    },
    {
      header: "Contracts.CurrentlyOnHand",
      field: "plafond.currentlyOnHand",
      fieldType: FieldType.NUMBER,
      hidden: true,
    },
    {
      header: "Contracts.ReservedPlafond",
      field: "plafond.reservedPlafond",
      fieldType: FieldType.NUMBER,
      hidden: true,
    },
    {
      header: "Contracts.CurrentlyUsed",
      field: "plafond.currentlyUsed",
      fieldType: FieldType.NUMBER,
      hidden: true,
    },
    {
      header: "Contracts.Plafond",
      field: "plafond.plafond",
      fieldType: FieldType.STRING,
    },
    {
      header: "Contracts.ToleranceAllowed",
      field: "plafond.isToleranceAllowed",
      fieldType: FieldType.BOOLEAN,
      hidden: true,
    },
    {
      header: "Contracts.TolerancePlafond",
      field: "plafond.tolerancePlafond",
      fieldType: FieldType.NUMBER,
    },
    {
      header: "Contracts.CustomerCode",
      field: "customer.customerCode",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "Contracts.CustomerVatin",
      field: "customer.vatin",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Contracts.CustomerName",
      field: "customer.name",
      fieldType: FieldType.STRING,
    },
    {
      header: "Contracts.StartedOn",
      field: "startedOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
      noOffset: true,
    },
    {
      header: "Contracts.EndedOn",
      field: "endedOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
      noOffset: true,
    },
    {
      header: "Contracts.CreatedOn",
      field: "createdOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
      hidden: true,
    },
    {
      header: "Contracts.UpdatedOn",
      field: "updatedOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
      hidden: true,
    },
  ];

  constructor(public store: Store<AppState>) {
    this.isLoading$ = this.store.pipe(select(getLoadingContractsState));
    this.contractList$ = this.store.pipe(select(loadedContractsListState));
    this.excelContractList$ = this.store.pipe(select(contractsExcelListState));
    this.totalContracts$ = this.store.pipe(select(contractsTotalState));
  }

  setTableCustomElements() {
    this.loadedContractSub = this.contractList$
      .pipe(skip(1))
      .subscribe((contracts: any[]) => {
        this.contractList = contracts;
        if (
          this.contractList !== undefined &&
          this.contractList !== null &&
          this.contractList?.length > 0
        ) {
          this.contractList = this.contractList.map((contract) => {
            let treatedContract = contract;
            treatedContract = this.setCustomStatusChip(treatedContract);
            treatedContract = this.setPlafondStatusChip(treatedContract);
            treatedContract = this.setCustomOnHandProgress(treatedContract);
            return treatedContract;
          });
        }
      });
  }

  setPlafondStatusChip(contract: any) {
    const defaultContract = {
      ...contract,
    };
    const { plafond } = contract;

    if (plafond === undefined) {
      return defaultContract;
    }

    const { plafondRuleCode, plafondRuleDescription } = plafond;

    if (
      plafondRuleDescription !== undefined &&
      plafondRuleDescription !== null
    ) {
      let style: string = "";
      let icon: string = "schedule";
      if (plafondRuleCode !== undefined && plafondRuleCode !== null) {
        switch (plafondRuleCode) {
          case "CHECKING_ACCOUNT":
            style =
              "background-color: rgb(209 209 209); color: rgb(109 109 109);";
            icon = "history_toggle_off";
            break;
          case "WEEKLY_RENEWAL":
            style = "background-color: rgb(235, 250, 220); color: #000";
            break;
          case "MONTHLY_RENEWAL":
            style = "background-color: rgb(200, 224, 175); color: #000";
            break;
        }
      }

      return {
        ...contract,
        plafond: {
          ...plafond,
          plafondRuleDescription: {
            value: plafondRuleDescription,
            style: style,
            icon: icon,
          },
        },
      };
    }
  }

  setCustomStatusChip(contract: any) {
    const defaultContract = {
      ...contract,
    };
    const { contractOverallStatusCode, contractOverallStatusDescription } =
      contract;

    if (contractOverallStatusDescription === undefined) {
      return defaultContract;
    }

    if (
      contractOverallStatusDescription !== undefined &&
      contractOverallStatusDescription !== null
    ) {
      let style: CHIP_STYLE = CHIP_STYLE.INACTIVE;
      if (
        contractOverallStatusCode !== undefined &&
        contractOverallStatusCode !== null
      ) {
        switch (contractOverallStatusCode) {
          case "ACTIVE":
            style = CHIP_STYLE.SUCCESS;
            break;

          case "DISABLED":
          case "CUSTOMER_INACTIVE":
          case "NOT_STARTED":
            style = CHIP_STYLE.INACTIVE;
            break;

          case "LOW CONTRACT_LOW_PLAFOND":
          case "LOW LOW_PLAFOND":
          case "CONTRACT_WARNING":
          case "EXPIRED":
            style = CHIP_STYLE.WARNING;
            break;

          case "BLOCKED":
          case "CUSTOMER_BLOCKED":
            style = CHIP_STYLE.DANGER;
            break;
        }
      }

      let styleSettings = getChipStyle(style);
      return {
        ...contract,
        contractOverallStatusDescription: {
          value: contractOverallStatusDescription,
          style: styleSettings.style,
          icon: styleSettings.icon,
        },
      };
    }
  }

  setCustomOnHandProgress(contract: any) {
    const defaultContract = {
      ...contract,
      availablePlafond: {
        value: 0,
        ngClass: {},
      },
    };
    const { plafond, contractOverallStatusIsActive } = contract;

    if (!plafond) {
      return defaultContract;
    }

    if (!plafond.currentlyOnHand || !plafond.plafond || plafond.plafond == 0) {
      return defaultContract;
    }
    let valuePerc = plafond.availablePercentage;
    const minMinStockPerc = 10;
    const minStockPerc = 30;

    if (valuePerc < 10) valuePerc = 10;

    return {
      ...contract,
      availablePlafond: {
        value: valuePerc,
        ngClass: {
          //"gray-progress": !contractOverallStatusIsActive, Not sure if we should use this...
          "red-progress": valuePerc <= minMinStockPerc,
          "orange-progress":
            valuePerc > minMinStockPerc && valuePerc < minStockPerc,
          "green-progress": valuePerc >= minStockPerc,
        },
        tooltip: plafond.currentlyOnHand + "/" + plafond.plafond,
      },
    };
  }

  getTableConfig(usageCode: string) {
    this.store.dispatch(
      getContractTableConfig({
        options: {
          code: usageCode,
          settingType: ConfigSettingType.TABLE_CONFIG,
        },
      })
    );
  }

  getTableData(tableEvent: TableEvent, getContracts: Function) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = tableEvent;
    getContracts(pageEvent, sortEvent, searchEvent, filterEvent);
  }

  getExportData(exportEvent: ExportEvent, getContractsExcelList: Function) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = exportEvent;
    getContractsExcelList(pageEvent, sortEvent, searchEvent, filterEvent);
  }

  saveTableConfig(tableColumnInfo: ColumnSorterInfo[], usageCode: string) {
    this.store.dispatch(
      saveContractTableConfig({
        config: {
          code: usageCode,
          settingTypeCode: ConfigSettingType.TABLE_CONFIG,
          scope: ConfigScope.USER,
          value: { tableColumnInfo },
        },
      })
    );
  }
}
